// MobileCatalogSheet — the design's `.mcb-sheet` "Browse the catalog" bottom
// sheet. Header + search + Catalog/Item filters + a 2-column card grid. The
// floating basket pill is rendered by MobileRequestFlow (app-level), not here.
// Reuses the existing SearchInput / FilterDropdown / hooks.

import { Box, Flex, Icon } from "@chakra-ui/react";
import { TbApps, TbBook2, TbSearchOff, TbX } from "react-icons/tb";
import SearchInput from "components/ds/SearchInput";
import PloyButton from "components/ds/PloyButton";
import FilterDropdown from "../FilterDropdown";
import type { CatalogBrowse } from "../useCatalogBrowse";
import type { Basket } from "../useBasket";
import MobileSheet from "./MobileSheet";
import MobileCatalogCard from "./MobileCatalogCard";

const MobileCatalogSheet = ({
  isOpen,
  onClose,
  catalog,
  basket,
  activeResourceIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  catalog: CatalogBrowse;
  basket: Basket;
  /** resourceIds the member already holds active access to (badges the cards). */
  activeResourceIds?: Set<string>;
}) => {
  const hasFilters = Boolean(catalog.catalogFilter || catalog.appFilter);

  return (
    <MobileSheet isOpen={isOpen} onClose={onClose} label="Browse the catalog" fill>
      {/* Header */}
      <Flex align="center" gap="12px" flexShrink={0}>
        <Box
          flex="1"
          fontFamily="heading"
          fontSize="2xl" // 24px
          fontWeight={500}
          letterSpacing="-0.01em"
          lineHeight="1.2"
          color="neutral.950"
          sx={{ textWrap: "balance" }}
        >
          Browse the catalog
        </Box>
        <Box
          as="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          w="32px"
          h="32px"
          borderRadius="component.lg"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.700"
          flexShrink={0}
          transition="background 120ms"
          _hover={{ bg: "neutral.100" }}
        >
          <Icon as={TbX} boxSize="18px" />
        </Box>
      </Flex>

      {/* Search */}
      <Box flexShrink={0}>
        <SearchInput
          value={catalog.search}
          onChange={catalog.setSearch}
          placeholder="Search resources…"
        />
      </Box>

      {/* Filters */}
      <Flex align="center" gap="sm" flexWrap="wrap" flexShrink={0}>
        <FilterDropdown
          icon={<Icon as={TbBook2} boxSize="16px" />}
          label="Catalog"
          allLabel="All catalogs"
          value={catalog.catalogFilter}
          options={catalog.catalogNames.map((c) => ({ id: c.id, label: c.name }))}
          onChange={catalog.selectCatalog}
        />
        <FilterDropdown
          icon={<Icon as={TbApps} boxSize="16px" />}
          label="Item"
          allLabel="All items"
          value={catalog.appFilter}
          options={catalog.appsForCurrentCatalog.map((a) => ({
            id: a.id,
            label: a.name,
          }))}
          onChange={catalog.setAppFilter}
        />
        {hasFilters && (
          <PloyButton
            variant="ghost"
            size="sm"
            onClick={() => catalog.selectCatalog(null)}
            leftIcon={<Icon as={TbX} boxSize="14px" />}
            color="p.600"
            _hover={{ bg: "p.50", color: "p.700" }}
          >
            Clear filters
          </PloyButton>
        )}
      </Flex>

      {/* Body — scrollable 2-column grid */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        mx="-20px"
        px="20px"
        pt="2xs"
        pb="sm"
      >
        {catalog.filteredItems.length > 0 ? (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap="10px">
            {catalog.filteredItems.map((item) => (
              <MobileCatalogCard
                key={item.id}
                item={item}
                added={basket.isAdded(item.id)}
                onToggle={() => basket.toggle(item.id)}
                hasAccess={Boolean(
                  activeResourceIds?.has(
                    item.resource.selectableResource.resourceId,
                  ),
                )}
              />
            ))}
          </Box>
        ) : (
          <Flex
            direction="column"
            align="center"
            gap="10px"
            py="40px"
            px="16px"
            color="neutral.700"
            fontSize="md"
            textAlign="center"
          >
            <Icon as={TbSearchOff} boxSize="28px" color="neutral.400" />
            <Box>No resources match your filters</Box>
          </Flex>
        )}
      </Box>
    </MobileSheet>
  );
};

export default MobileCatalogSheet;
